import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';
import { addToastAtom } from '../../../atoms/v0';
import { logger } from '../../../lib/logger';

/**
 * Whisper API 语音识别钩子
 */
export const useWhisperRecognition = () => {
  const { t } = useTranslation(['v0/chat']);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartTimeRef = useRef<number>(0);
  const addToast = useSetAtom(addToastAtom);

  // 开始录音
  const startRecording = useCallback(async () => {
    try {
      // 重置状态
      audioChunksRef.current = [];
      setTranscript('');

      // 请求麦克风权限
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // 创建 MediaRecorder 实例
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      // 设置数据处理函数
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // 开始录音
      mediaRecorder.start();
      recordingStartTimeRef.current = Date.now(); // 记录开始时间
      setIsRecording(true);
    } catch (error) {
      console.error('Failed to start recording:', error);
      addToast({
        title: t('v0/chat:microphoneNotAvailable'),
        description: t('v0/chat:microphoneNotAvailableDescription'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [addToast, t]);

  // 停止录音并处理
  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      return;
    }

    return new Promise<string>((resolve) => {
      mediaRecorderRef.current!.onstop = async () => {
        let audioBlob: Blob | undefined;

        try {
          setIsProcessing(true);

          // 创建音频 Blob
          audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

          // 创建 FormData 对象
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');

          // 调用 Supabase Edge Function
          const response = await fetch('/whisper', {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) {
            throw new Error(`Server responded with ${response.status}`);
          }

          const data = await response.json();
          setTranscript(data.text);
          resolve(data.text);
        } catch (error) {
          console.error('Failed to process audio:', error);

          // 获取详细错误信息
          let errorDetails = '';

          if (error instanceof Error) {
            errorDetails = error.message;
          } else if (typeof error === 'object' && error !== null) {
            try {
              errorDetails = JSON.stringify(error);
            } catch {
              errorDetails = String(error);
            }
          } else {
            errorDetails = String(error);
          }

          // 记录错误到Axiom
          logger.error('Whisper语音识别失败(v0)', error instanceof Error ? error : new Error(errorDetails), {
            recordingDuration: Date.now() - recordingStartTimeRef.current,
            audioSize: audioBlob?.size || 0,
            platform: navigator.platform,
            userAgent: navigator.userAgent,
            whisperError: true,
            version: 'v0'
          });

          addToast({
            title: t('v0/chat:audioProcessingFailed'),
            description: `${t('v0/chat:audioProcessingFailedDescription')}${errorDetails ? `\n错误详情: ${errorDetails}` : ''}`,
            status: 'error',
            duration: 5000, // 延长显示时间
            isClosable: true,
          });
          resolve('');
        } finally {
          setIsProcessing(false);
          setIsRecording(false);

          // 停止所有音轨
          if (mediaRecorderRef.current) {
            const tracks = mediaRecorderRef.current.stream.getTracks();
            tracks.forEach(track => track.stop());
          }
        }
      };

      // 停止录音
      mediaRecorderRef.current!.stop();
    });
  }, [addToast, t]);

  // 取消录音
  const cancelRecording = useCallback(() => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      return;
    }

    // 停止录音
    mediaRecorderRef.current.stop();

    // 停止所有音轨
    const tracks = mediaRecorderRef.current.stream.getTracks();
    tracks.forEach(track => track.stop());

    // 重置状态
    audioChunksRef.current = [];
    setIsRecording(false);
  }, []);

  return {
    isRecording,
    isProcessing,
    transcript,
    startRecording,
    stopRecording,
    cancelRecording
  };
};
